import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { postRequest, getRequest } from "../Apis/apiWrapper";
import { paymentEndpoints } from "../utils/constants/httpConstants";
import EventEmitter from "../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
  COUNTRY_LIST,
} from "../utils/constants";
import {
  EMITTER_CREDIT_UPDATE,
  EMITTER_SDR_STATUS,
} from "../utils/creditMonitor/creditStatusCodes";
import { classifyPlanChange } from "../screens/ProfileScreen/components/Settings/Billing/billingUtils";

/**
 * useBillingData
 *
 * Centralises all billing-related state and API logic so that the
 * Billing screen component stays lean (UI-only).
 */
const useBillingData = () => {
  const navigate = useNavigate();

  // ─── Core data ──────────────────────────────────────────────────────
  const [packages, setPackages] = useState([]);
  const [topUps, setTopUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [changingPlanId, setChangingPlanId] = useState(null);
  const [isResuming, setIsResuming] = useState(false);
  const [isManagingPayment, setIsManagingPayment] = useState(false);
  const inFlightRef = useRef(false); // Fix R2: synchronous checkout double-click guard
  const [billingPlan, setBillingPlan] = useState(null);
  const [creditUsage, setCreditUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [takenOnceFreePlan, setTakenOnceFreePlan] = useState(false);
  const [isSdr, setIsSdr] = useState(false);

  // ─── Cancel modal ────────────────────────────────────────────────────
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isEndingTrial, setIsEndingTrial] = useState(false);
  const [loadingCancelInfo, setLoadingCancelInfo] = useState(false);
  const [confirmCancelText, setConfirmCancelText] = useState("");

  // ─── Change-plan confirmation modal ──────────────────────────────────
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null); // { plan, action, title, message, confirmLabel }
  const [isCancellingScheduledChange, setIsCancellingScheduledChange] = useState(false);

  // ─── Payment & address ───────────────────────────────────────────────
  const [billingAddress, setBillingAddress] = useState({
    company_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  });
  const [filteredCountries, setFilteredCountries] = useState(COUNTRY_LIST);
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  // ─── Settings toggles ────────────────────────────────────────────────
  const [toggles, setToggles] = useState({
    receipts: true,
    renewal: true,
    failed: true,
    threshold: true,
    autoPurchase: false,
  });

  // ─── Initial data load ───────────────────────────────────────────────
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        // Fix R5: allSettled so one fetcher failing can never abort the others (resilient load).
        // Fix R4: fetchAutoTopUp dropped from the load — the BE auto-topup endpoint was removed (A8),
        // so calling it just 404s; the toggle UI is also gated off in BillingSettingsSection.
        await Promise.allSettled([
          fetchProducts(),
          fetchCurrentSubscription(),
          fetchBillingPlan(),
          fetchCreditUsage(),
          fetchInvoices(),
          fetchBillingAddress(),
          fetchSecondaryEmail(),
        ]);
      } catch (error) {
        console.error("Error fetching initial billing data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── API: fetch products ─────────────────────────────────────────────
  const fetchProducts = async () => {
    try {
      const response = await postRequest(
        navigate,
        paymentEndpoints.listProducts,
        { limit: 1000 },
      );

      if (response?.data?.status && response?.data?.data?.packages) {
        const packagesData = response.data.data.packages;
        setTakenOnceFreePlan(response?.data?.data?.takenOnceFreePlan || false);
        const formattedPackages = packagesData.map((pkg, index) => ({
          id: pkg._id,
          name: pkg.name || `Package ${index + 1}`,
          description: pkg.description || "",
          price: pkg.amount || 0,
          currency: pkg.currency?.toUpperCase() || "USD",
          featured: pkg.name === "Standard" || index === 1,
          interval: pkg.billing_period || "month",
          stripe_price_id: pkg.stripe_price_id,
          credits: pkg.credits || 0,
          features: pkg.features || {},
          isFree: pkg.isFree || false,
          isSdr: pkg.isSdr || false,
          isDashboard: pkg.isDashboard || false,
          isCrm: pkg.isCrm || false,
        }));
        setPackages(formattedPackages);

        const topUpsData = response.data.data.top_ups || [];
        const formattedTopUps = topUpsData
          .map((pkg) => ({
            id: pkg._id,
            name: pkg.name || "Top Up",
            price: pkg.amount || 0,
            currency: pkg.currency?.toUpperCase() || "USD",
            stripe_price_id: pkg.stripe_price_id,
            credits: pkg.credits || 0,
            isTopUp: true,
          }))
          .filter((t) => t.credits > 0)
          .sort((a, b) => a.credits - b.credits);
        setTopUps(formattedTopUps);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // ─── API: current subscription ───────────────────────────────────────
  const fetchCurrentSubscription = async () => {
    try {
      const response = await getRequest(
        navigate,
        paymentEndpoints.currentSubscription,
      );
      if (response?.data?.status && response?.data?.data?.has_subscription) {
        const data = response.data.data;
        const sub = data.subscription;
        // Attach any pending scheduled plan change (downgrade) so the UI can show a banner.
        if (sub) {
          sub.pending_plan = data.pending_plan || null;
          sub.plan_change_effective_at = data.plan_change_effective_at || null;
        }
        setCurrentSubscription(sub);
        // Extract isSdr from subscription metadata (comes as string "true")
        const sdrFlag = sub?.metadata?.isSdr;
        if (sdrFlag === true || sdrFlag === "true") {
          setIsSdr(true);
          EventEmitter.emit(EMITTER_SDR_STATUS, true);
        }
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  // ─── API: billing plan ───────────────────────────────────────────────
  const fetchBillingPlan = async () => {
    try {
      const response = await getRequest(navigate, paymentEndpoints.billingPlan);
      if (response?.data?.status) {
        setBillingPlan(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching billing plan:", error);
    }
  };

  // ─── API: credit usage ───────────────────────────────────────────────
  const fetchCreditUsage = async () => {
    try {
      const response = await getRequest(navigate, paymentEndpoints.creditUsage);
      if (response?.data?.status) {
        const data = response.data.data;
        setCreditUsage(data);
        // Also check isSdr from credit usage API (comes as boolean)
        if (data?.isSdr === true || data?.isSdr === "true") {
          setIsSdr(true);
          EventEmitter.emit(EMITTER_SDR_STATUS, true);
        }
      }
    } catch (error) {
      console.error("Error fetching credit usage:", error);
    }
  };

  // ─── Live credit updates ─────────────────────────────────────────────
  // creditInterceptor emits EMITTER_CREDIT_UPDATE with the remaining_credits
  // found in any API response (reveals, exports, ...), so refetch the usage
  // breakdown to keep the Overview current while Billing is mounted.
  //
  // The credit-usage response itself carries remaining_credits, so it re-emits
  // this event. We must NOT compare against React state (creditUsage) here: the
  // axios interceptor emits during response handling, before fetchCreditUsage's
  // setCreditUsage has committed, so a state-based guard races and can schedule
  // a redundant refetch. Instead track the last value we've already acted on in
  // a ref (updated synchronously the moment we decide to fetch), so the
  // self-triggered re-emit is recognised and dropped deterministically.
  const lastSeenCreditsRef = useRef(null);
  useEffect(() => {
    let timer = null;
    const handleCreditUpdate = (credits) => {
      if (lastSeenCreditsRef.current === credits) return;
      lastSeenCreditsRef.current = credits;
      clearTimeout(timer);
      timer = setTimeout(() => fetchCreditUsage(), 800);
    };
    EventEmitter.on(EMITTER_CREDIT_UPDATE, handleCreditUpdate);
    return () => {
      clearTimeout(timer);
      EventEmitter.removeEventListener(
        EMITTER_CREDIT_UPDATE,
        handleCreditUpdate,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── API: invoices ───────────────────────────────────────────────────
  const fetchInvoices = async () => {
    try {
      const response = await getRequest(
        navigate,
        paymentEndpoints.listInvoices + "?limit=100",
      );
      if (response?.data?.status) {
        setInvoices(response.data.data.invoices || []);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  // ─── API: auto top-up ────────────────────────────────────────────────
  const fetchAutoTopUp = async () => {
    try {
      const response = await getRequest(navigate, paymentEndpoints.autoTopUp);
      if (response?.data?.status) {
        setToggles((prev) => ({
          ...prev,
          autoPurchase: response.data.data.enabled,
        }));
      }
    } catch (error) {
      console.error("Error fetching auto top-up:", error);
    }
  };

  // ─── API: billing address ────────────────────────────────────────────
  const fetchBillingAddress = async () => {
    try {
      const response = await getRequest(
        navigate,
        paymentEndpoints.billingAddress,
      );
      if (response?.data?.status) {
        setBillingAddress(
          response.data.data || {
            company_name: "",
            tax_id: "",
            address_line1: "",
            address_line2: "",
            city: "",
            state: "",
            postal_code: "",
            country: "",
          },
        );
      }
    } catch (error) {
      console.error("Error fetching billing address:", error);
    }
  };

  // ─── API: secondary email ────────────────────────────────────────────
  const fetchSecondaryEmail = async () => {
    try {
      const response = await getRequest(
        navigate,
        paymentEndpoints.secondaryEmail,
      );
      if (response?.data?.status) {
        setSecondaryEmail(response.data.data.secondary_email || "");
      }
    } catch (error) {
      console.error("Error fetching secondary email:", error);
    }
  };

  // ─── Handler: buy package ────────────────────────────────────────────
  const handleBuyPackage = useCallback(
    async (plan) => {
      // Fix R2: synchronous in-flight latch — reject a rapid double-click immediately (the
      // buyingPlanId disabled-state only applies after a React re-render). Outer finally always resets it.
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        if (plan.isFree) {
          if (takenOnceFreePlan) {
            EventEmitter.emit(EMITTER_ERROR, "You have already used your free quota");
            return;
          }
          try {
            setBuyingPlanId(plan.id);
            const response = await postRequest(
              navigate,
              paymentEndpoints.claimFreePlan,
              { package_id: plan.id },
            );
            if (response?.data?.status) {
              EventEmitter.emit(
                EMITTER_SUCCESS,
                response?.data?.message || "Free plan activated successfully.",
              );
              fetchCurrentSubscription();
              fetchBillingPlan();
              fetchCreditUsage();
            } else {
              EventEmitter.emit(
                EMITTER_ERROR,
                response?.data?.message || "Failed to activate free plan.",
              );
            }
          } catch (error) {
            console.error("Error claiming free plan:", error);
            EventEmitter.emit(
              EMITTER_ERROR,
              error?.data?.message || "Failed to activate free plan.",
            );
          } finally {
            setBuyingPlanId(null);
          }
          return;
        }

        if (!plan.stripe_price_id) {
          EventEmitter.emit(EMITTER_ERROR, "This package is not available for purchase.");
          return;
        }

        // Fix R1: a user with an active subscription must not start a FRESH checkout for another
        // plan — it would create a 2nd subscription (the BE rejects it, BE-B2, leaving a dead-end
        // Stripe redirect). Top-ups (isTopUp) are exempt — an active-plan user can still buy credits.
        const hasActiveSub = ["active", "trialing"].includes(currentSubscription?.status);
        if (!plan.isTopUp && hasActiveSub) {
          EventEmitter.emit(
            EMITTER_ERROR,
            "You already have an active plan. Use the upgrade/downgrade option to change plans.",
          );
          return;
        }

        try {
          setBuyingPlanId(plan.id);
          const response = await postRequest(
            navigate,
            paymentEndpoints.createCheckoutSession,
            { price_id: plan.stripe_price_id },
          );
          if (response?.data?.status && response?.data?.data?.checkout_url) {
            window.location.href = response.data.data.checkout_url;
          } else {
            EventEmitter.emit(
              EMITTER_ERROR,
              response?.data?.message || "Failed to create checkout session.",
            );
          }
        } catch (error) {
          console.error("Error creating checkout session:", error);
          EventEmitter.emit(
            EMITTER_ERROR,
            error?.data?.message || "Failed to initiate payment. Please try again.",
          );
        } finally {
          setBuyingPlanId(null);
        }
      } finally {
        inFlightRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [takenOnceFreePlan, currentSubscription],
  );

  // ─── Handler: toggle ─────────────────────────────────────────────────
  const handleToggle = useCallback(
    async (key) => {
      const newValue = !toggles[key];
      if (key === "autoPurchase") {
        try {
          const response = await postRequest(
            navigate,
            paymentEndpoints.autoTopUp,
            { enabled: newValue },
          );
          if (response?.data?.status) {
            setToggles((prev) => ({ ...prev, [key]: newValue }));
            EventEmitter.emit(EMITTER_SUCCESS, response.data.message);
          }
        } catch (error) {
          console.error("Error toggling auto top-up:", error);
          EventEmitter.emit(EMITTER_ERROR, "Failed to update auto top-up setting.");
        }
      } else {
        setToggles((prev) => ({ ...prev, [key]: newValue }));
      }
    },
    [toggles, navigate],
  );

  // ─── Handler: cancel subscription ────────────────────────────────────
  const fetchCancelInfo = async () => {
    try {
      setLoadingCancelInfo(true);
      const response = await getRequest(
        navigate,
        paymentEndpoints.cancelSubscriptionInfo,
      );
      if (response?.data?.status) {
        setCancelInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching cancel info:", error);
    } finally {
      setLoadingCancelInfo(false);
    }
  };

  const handleCancelClick = useCallback(async () => {
    await fetchCancelInfo();
    setConfirmCancelText("");
    setCancelModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancelSubscription = useCallback(async () => {
    try {
      setIsCancelling(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.cancelSubscription,
        { subscription_id: currentSubscription?.id },
      );
      if (response?.data?.status) {
        EventEmitter.emit(
          EMITTER_SUCCESS,
          response?.data?.message || "Subscription cancelled successfully.",
        );
        setCancelModalOpen(false);
        fetchCurrentSubscription();
        fetchBillingPlan();
        // fetchAutoTopUp() removed — the BE endpoint was deleted (dead feature); this
        // was the last live caller and 404'd in the console after every cancel.
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          response?.data?.message || "Failed to cancel subscription.",
        );
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "An error occurred during cancellation.",
      );
    } finally {
      setIsCancelling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubscription]);

  // ─── Handler: end the trial NOW ("Bill me now") — early-convert to a paid subscription ─────
  // Charges the saved card immediately; the sub flips trialing -> active and the BE grants the
  // full month credits. Refresh sub + plan so the UI reflects the new state right away.
  const handleEndTrial = useCallback(async () => {
    try {
      setIsEndingTrial(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.endTrial,
        { subscription_id: currentSubscription?.id },
      );
      if (response?.data?.status) {
        EventEmitter.emit(
          EMITTER_SUCCESS,
          response?.data?.message || "Your subscription is now active.",
        );
        // Early-convert changes EVERYTHING within seconds (backend verified: charge +
        // credits land ~5s after the click) — refetch it all, else the page shows
        // stale trial credits and no invoice until the next full reload.
        fetchCurrentSubscription();
        fetchBillingPlan();
        fetchCreditUsage();
        fetchInvoices();
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          response?.data?.message || "Could not start your subscription. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error ending trial:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "An error occurred while starting your subscription.",
      );
    } finally {
      setIsEndingTrial(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubscription]);

  // ─── Handler: change plan (upgrade / downgrade an ACTIVE subscription) ─────────
  const handleChangePlan = useCallback(
    async (plan) => {
      if (!plan?.stripe_price_id) {
        EventEmitter.emit(EMITTER_ERROR, "This plan is not available.");
        return;
      }
      // Synchronous double-fire guard (mirrors handleBuyPackage) — a rapid double-confirm must not
      // fire two change-plan requests. The BE per-sub lock 409s a concurrent one too; this stops it
      // client-side. NOTE: set here INSIDE handleChangePlan, never in confirmPlanChange, else the
      // shared ref would also block the "buy" path that delegates to handleBuyPackage.
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        setChangingPlanId(plan.id);
        // Send the current subscription id + the target price with the field names the BE expects.
        // (BE also tolerates a missing subscription_id / a price_id alias, but be explicit here.)
        const response = await postRequest(navigate, paymentEndpoints.changePlan, {
          subscription_id: currentSubscription?.id,
          new_price_id: plan.stripe_price_id,
        });
        if (response?.data?.status) {
          EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message || "Your plan has been updated.");
          fetchCurrentSubscription();
          fetchBillingPlan();
          fetchCreditUsage(); // upgrade SETs credits now → refresh the balance shown (else stale)
        } else {
          EventEmitter.emit(EMITTER_ERROR, response?.data?.message || "Failed to change plan.");
        }
      } catch (error) {
        console.error("Error changing plan:", error);
        EventEmitter.emit(EMITTER_ERROR, error?.data?.message || "Failed to change plan. Please try again.");
      } finally {
        setChangingPlanId(null);
        inFlightRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, currentSubscription],
  );

  // ─── Handler: request a plan change (classify + open the confirmation modal) ───
  // A plan card no longer commits on click — it opens a modal that spells out exactly what will
  // happen (upgrade = immediate; downgrade = at period end; buy = checkout), so accidental clicks
  // can't change a plan.
  const requestPlanChange = useCallback(
    (plan) => {
      if (!plan) return;
      const isThisCurrent =
        currentSubscription?.plan?.id === plan?.stripe_price_id &&
        currentSubscription?.status === "active";
      if (isThisCurrent) return; // already on this plan — no-op
      const info = classifyPlanChange(currentSubscription, plan, packages);
      if (info.action === "noop") return;
      setPendingChange({ ...info, plan });
      setChangeModalOpen(true);
    },
    [currentSubscription, packages],
  );

  // ─── Handler: confirm the pending plan change (fires the real mutation) ────────
  const confirmPlanChange = useCallback(() => {
    if (!pendingChange?.plan) return;
    const plan = pendingChange.plan;
    setChangeModalOpen(false);
    if (pendingChange.action === "buy") {
      handleBuyPackage(plan);
    } else {
      handleChangePlan(plan);
    }
  }, [pendingChange, handleBuyPackage, handleChangePlan]);

  // ─── Handler: cancel a PENDING scheduled plan change (undo a scheduled downgrade) ──
  const cancelScheduledChange = useCallback(async () => {
    try {
      setIsCancellingScheduledChange(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.cancelScheduledChange,
        {},
      );
      if (response?.data?.status) {
        EventEmitter.emit(
          EMITTER_SUCCESS,
          response?.data?.message || "Scheduled change cancelled.",
        );
        fetchCurrentSubscription();
        fetchBillingPlan();
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          response?.data?.message || "Failed to cancel scheduled change.",
        );
      }
    } catch (error) {
      console.error("Error cancelling scheduled change:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to cancel scheduled change.",
      );
    } finally {
      setIsCancellingScheduledChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ─── Handler: resume / reactivate a sub set to cancel-at-period-end ────────────
  const handleResumeSubscription = useCallback(async () => {
    try {
      setIsResuming(true);
      const response = await postRequest(navigate, paymentEndpoints.resumeSubscription, {
        subscription_id: currentSubscription?.id,
      });
      if (response?.data?.status) {
        EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message || "Your subscription has been reactivated.");
        fetchCurrentSubscription();
        fetchBillingPlan();
      } else {
        EventEmitter.emit(EMITTER_ERROR, response?.data?.message || "Failed to reactivate subscription.");
      }
    } catch (error) {
      console.error("Error resuming subscription:", error);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message || "Failed to reactivate. Please try again.");
    } finally {
      setIsResuming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, currentSubscription]);

  // ─── Handler: open Stripe billing portal to update a failed/expired card (dunning) ──
  // NOTE: requires the payment service /subscription/portal route to be ENABLED (it was
  // disabled under B7 to force in-app flows). Re-enable it so dunning users can update a card.
  const handleManagePayment = useCallback(async () => {
    try {
      setIsManagingPayment(true);
      const response = await getRequest(navigate, paymentEndpoints.billingPortal);
      const url = response?.data?.data?.url || response?.data?.data?.portal_url;
      if (response?.data?.status && url) {
        window.location.href = url;
      } else {
        EventEmitter.emit(EMITTER_ERROR, response?.data?.message || "Could not open billing management. Please contact support to update your card.");
      }
    } catch (error) {
      console.error("Error opening billing portal:", error);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message || "Could not open billing management. Please contact support to update your card.");
    } finally {
      setIsManagingPayment(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ─── Handler: save address ───────────────────────────────────────────
  const handleSaveAddress = useCallback(async () => {
    const requiredFields = [
      { key: "company_name", label: "Company Name" },
      { key: "address_line1", label: "Address Line 1" },
      { key: "city", label: "City" },
      { key: "state", label: "State / Province" },
      { key: "postal_code", label: "Postal Code" },
      { key: "country", label: "Country" },
    ];

    const sanitizedAddress = Object.entries(billingAddress).reduce(
      (acc, [key, value]) => {
        acc[key] = typeof value === "string" ? value.trim() : value;
        return acc;
      },
      {},
    );

    const missing = requiredFields.find(
      ({ key }) => !sanitizedAddress[key],
    );

    if (missing) {
      EventEmitter.emit(EMITTER_ERROR, `${missing.label} is required`);
      return;
    }

    try {
      setIsSavingAddress(true);
      setBillingAddress(sanitizedAddress);
      const response = await postRequest(
        navigate,
        paymentEndpoints.billingAddress,
        sanitizedAddress,
      );
      if (response?.data?.status) {
        EventEmitter.emit(EMITTER_SUCCESS, response.data.message);
      }
    } catch (error) {
      console.error("Error saving billing address:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to save address",
      );
    } finally {
      setIsSavingAddress(false);
    }
  }, [billingAddress, navigate]);

  // ─── Handler: save email ─────────────────────────────────────────────
  const handleSaveEmail = useCallback(async () => {
    try {
      setIsSavingEmail(true);
      const response = await postRequest(
        navigate,
        paymentEndpoints.secondaryEmail,
        { secondary_email: secondaryEmail },
      );
      if (response?.data?.status) {
        EventEmitter.emit(EMITTER_SUCCESS, response.data.message);
      } else {
        EventEmitter.emit(EMITTER_ERROR, response.data.message);
      }
    } catch (error) {
      console.error("Error saving secondary email:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to save email",
      );
    } finally {
      setIsSavingEmail(false);
    }
  }, [secondaryEmail, navigate]);

  // ─── Handler: country search ─────────────────────────────────────────
  const handleCountrySearch = useCallback((event) => {
    const searchText = event?.target?.value || "";
    if (!searchText) {
      setFilteredCountries(COUNTRY_LIST);
    } else {
      const filtered = COUNTRY_LIST.filter((country) =>
        country.label.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredCountries(filtered);
    }
  }, []);

  // ─── Public API ──────────────────────────────────────────────────────
  return {
    // data
    packages,
    topUps,
    loading,
    currentSubscription,
    buyingPlanId,
    changingPlanId,
    isResuming,
    isManagingPayment,
    billingPlan,
    creditUsage,
    invoices,
    takenOnceFreePlan,
    isSdr,
    // cancel modal
    cancelModalOpen,
    cancelInfo,
    isCancelling,
    isEndingTrial,
    loadingCancelInfo,
    confirmCancelText,
    // change-plan confirmation modal
    changeModalOpen,
    pendingChange,
    isCancellingScheduledChange,
    // payment / address
    billingAddress,
    filteredCountries,
    secondaryEmail,
    isSavingAddress,
    isSavingEmail,
    toggles,
    // setters consumed by child components
    setCancelModalOpen,
    setConfirmCancelText,
    setChangeModalOpen,
    setBillingAddress,
    setSecondaryEmail,
    // handlers
    handleBuyPackage,
    handleToggle,
    handleCancelClick,
    handleCancelSubscription,
    handleChangePlan,
    requestPlanChange,
    confirmPlanChange,
    cancelScheduledChange,
    handleResumeSubscription,
    handleEndTrial,
    handleManagePayment,
    handleSaveAddress,
    handleSaveEmail,
    handleCountrySearch,
  };
};

export default useBillingData;
